import { useEffect, useRef } from "react";
import { motion, useAnimationControls } from "framer-motion";
import { MAX_HEARTS } from "@/config/gameConfig";
import HeartIcon from "@/components/icons/HeartIcon";

type Props = {
    className?: string;
    heartCount: number;
    animate?: boolean;
};

const AnimatedHeart = ({
    filled,
    index,
    shouldAnimate,
}: {
    filled: boolean;
    index: number;
    shouldAnimate: boolean;
}) => {
    const controls = useAnimationControls();
    const prevFilled = useRef(filled);

    useEffect(() => {
        if (!shouldAnimate) {
            prevFilled.current = filled;
            return;
        }

        if (filled !== prevFilled.current) {
            if (filled) {
                // Heart gained: pop in
                controls.start({
                    scale: [1, 1.4, 1],
                    transition: { duration: 0.3, ease: "easeOut" },
                });
            } else {
                // Heart lost: shrink pulse
                controls.start({
                    scale: [1, 0.6, 1],
                    opacity: [1, 0.4, 1],
                    transition: { duration: 0.25, ease: "easeIn" },
                });
            }
        }

        prevFilled.current = filled;
    }, [filled, shouldAnimate, controls]);

    return (
        <motion.div animate={controls}>
            <HeartIcon
                className={filled ? "fill-danger-500" : "stroke-danger-700"}
                id={index}
                width={16}
                height={16}
            />
        </motion.div>
    );
};

const HeartDisplay = ({ heartCount, animate = true }: Props) => {
    const hasMounted = useRef(false);

    useEffect(() => {
        hasMounted.current = true;
    }, []);

    return (
        <div className="flex flex-row items-start w-full justify-between">
            {Array.from({ length: MAX_HEARTS }).map((_, index) => (
                <AnimatedHeart
                    key={index}
                    index={index}
                    filled={index + 1 <= heartCount}
                    shouldAnimate={animate && hasMounted.current}
                />
            ))}
        </div>
    );
};

export default HeartDisplay;